/**
 * All tasks.
 *
 * A flat, sortable list of every task in the workspace. The scope filter
 * controls what "every" means:
 *
 *   - Open (default): active, undone. The working backlog.
 *   - All:            includes archived and done. Useful for searching.
 *   - Done:           completed but not archived.
 *   - Archived:       soft-deleted.
 *
 * Sort now lives in the section header (via `useSectionSorts`) rather than
 * the page, and defaults to priority (severity order), then due date
 * ascending with NULLs last. Keys the server can express (`serverSortFor`)
 * are sent as `sort=` so the fetched 500-row window matches what's being
 * sorted. The rest — person, project, area, status — leave the window
 * exactly where it is and sort the cached rows client-side, so picking one
 * never triggers a refetch. See the comment on `serverSort` below for why
 * that isn't a fixed fallback.
 */

import { useEffect, useState } from 'react';
import { useTasksAll, type TasksScope, type TasksSort } from '../api/tasks';
import { TaskSection } from '../components/TaskSection';
import { serverSortFor, useSectionSorts, type TaskSort } from '../lib/task-sort';
import { useToggleTask } from './_use-open-task';
import { useTaskQuickActions } from './_use-task-quick-actions';
import styles from './Lists.module.css';

interface ScopeOption {
  id: TasksScope;
  label: string;
}

const SCOPE_OPTIONS: ScopeOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'all', label: 'All' },
  { id: 'done', label: 'Done' },
  { id: 'archived', label: 'Archived' },
];

const SCOPE_BLURBS: Record<TasksScope, string> = {
  open: 'Active backlog — everything still to do.',
  all: 'Every task in the workspace, including completed and archived.',
  done: 'Completed tasks (not archived).',
  archived: 'Archived tasks — kept for history.',
};

export function AllTasksScreen() {
  const [scope, setScope] = useState<TasksScope>('open');
  const sorts = useSectionSorts({ 'all.tasks': 'priority' });
  const sort = sorts.sortFor('all.tasks');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Debounce the search box so we don't refetch on every keystroke. Cleared on
  // unmount / each edit so no stale fetch fires. The explicit `sort` chip still
  // wins over the server's q-recency default because we always pass `sort`.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);
  // The server window is tracked SEPARATELY from the chosen sort. Keys the
  // server can express move the window so the 500-row cap matches what we're
  // sorting; the client-only keys (person/project/area/status) leave it exactly
  // where it is, so picking one re-sorts the rows already in cache instead of
  // triggering a refetch and a loading flash. Falling back to a fixed key here
  // would refetch whenever the current window wasn't that key — e.g. after
  // switching scope to Done and then sorting by person.
  const [serverSort, setServerSort] = useState<TasksSort>(
    () => serverSortFor(sort.key) ?? 'priority',
  );
  const changeSort = (next: TaskSort) => {
    const expressible = serverSortFor(next.key);
    if (expressible) setServerSort(expressible);
    sorts.setSortFor('all.tasks', next);
  };
  const query = useTasksAll(scope, serverSort, debouncedSearch);
  const complete = useToggleTask();
  const quick = useTaskQuickActions();

  const tasks = query.data?.tasks ?? [];

  return (
    <>
      <div className={styles.head}>
        <h2 className={styles.title}>All tasks</h2>
        <div className={styles.sub}>{SCOPE_BLURBS[scope]}</div>
      </div>

      <input
        type="search"
        className={styles.searchInput}
        placeholder="Search tasks…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search tasks"
      />

      <div className="kos-filterbar">
        {SCOPE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`kos-filter-chip ${
              scope === opt.id ? 'kos-filter-chip-active' : ''
            }`}
            onClick={() => setScope(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {query.isLoading && <div className={styles.loading}>Loading…</div>}
      {query.isError && (
        <div className={styles.error}>Couldn’t load tasks.</div>
      )}

      {!query.isLoading && !query.isError && tasks.length === 0 && (
        <div className={styles.empty}>
          {debouncedSearch.trim().length >= 2
            ? `No tasks match “${debouncedSearch.trim()}”.`
            : scope === 'open'
              ? 'Nothing on your plate — enjoy the calm.'
              : 'No tasks in this view.'}
        </div>
      )}

      <TaskSection
        title="Tasks"
        tasks={tasks}
        alert={
          sort.key === 'due' && (scope === 'open' || scope === 'all')
            ? 'undated tasks at the bottom'
            : null
        }
        showStatus
        rowOptions={{ showUpdatedSince: sort.key === 'updated' }}
        sort={sort}
        onSortChange={changeSort}
        onComplete={complete}
        quick={quick}
      />
    </>
  );
}
